import * as React from 'react';
import { FeedbackLink } from './feedback-link';
import { AccountData } from 'core/models/user-manager';
import __ from '@core/lib/localization';
import { AppFactory } from '@app/app-factory';

export type Message = {
  title: React.ReactNode;
  body: React.ReactNode | (() => React.ReactNode);
  type?: 'error' | 'success' | 'warning';
  showIcon?: boolean;
};

export const getMessages = (accountData: AccountData) => {
  // const paymentData = accountData.paymentData;

  const { l2Localized } = AppFactory.root;

  const messages: Record<string, Message> = {
    'extending-subscribe-success': {
      type: 'success',
      title: __('Subscription successful!', 'subscriptionSuccessful'),
      body: __(
        "You're ready to go. You can manage your subscription from your account page.",
        'readyToGoCanManageSubscription'
      ),
    },

    'extending-full-access': {
      type: 'success',
      title: __('Success!', 'successExclamation'),
      body: __(
        "You're ready to go. You can manage your subscription from your account page.",
        'readyToGoCanManageSubscription'
      ),
    },

    'auto-renew-cancelled-with-days-left': {
      type: 'success',
      title: __(
        'Your subscription has been canceled.',
        'subscriptionCancelled'
      ),
      body: () => (
        // todo: fix this for node entitlements
        // @armando, how do we localize this?
        <>
          Thank you for trying Jiveworld. You will continue to have full access
          until {accountData.fullAccessUntil}. Please take a moment to share
          your <FeedbackLink>your feedback</FeedbackLink> — we'd love to know
          what we could improve.
        </>
      ),
    },

    'auto-renew-cancelled-no-days-left': {
      type: 'success',
      title: 'Your subscription has been canceled.',
      body: () => (
        <>
          Thank you for trying Jiveworld. Please take a moment to share your{' '}
          <FeedbackLink>your feedback</FeedbackLink> — we'd love to know what we
          could improve.
        </>
      ),
    },

    // @frank, here's another confirmation modal which I assume you would similarly trimmed.
    // actually, i'm not sure if that can still be reached or not. applying a coupon resulted
    // in a different message.
    'first-full-access': {
      type: 'success',
      title: __("You're ready to go!", 'youreReadyToGoExclamation'),
      body: __(
        `Congratulations! You're on the last leg of your journey to real-world %{l2Localized} fluency. All stories are unlocked on your account.`,
        'congratulationsAllStoriesUnlocked',
        { l2Localized }
      ),
    },

    'first-subscribe-success': {
      type: 'success',
      title: "You're subscribed!",
      body: __(
        `Congratulations! You're on the last leg of your journey to real-world %{l2Localized} fluency. All stories are unlocked on your account.`,
        'congratulationsAllStoriesUnlocked',
        { l2Localized }
      ),
    },

    'card-update-success': {
      type: 'success',
      title: __('Update successful!', 'updateSuccessful'),
      body: __("You're ready to go", 'youreReadyToGo'),
    },

    'auto-renew-failure': {
      type: 'error',
      title: __('Payment unsuccessful', 'paymentUnsuccessful'),
      body: '',
    },

    'auto-renew-failure-with-access': {
      type: 'error',
      title: __('Payment unsuccessful', 'paymentUnsuccessful'),
      // body: `We were unable to charge your card ending ${paymentData?.cardLastFour} on ${accountData.fullAccessUntil}. To ensure uninterrupted access to all stories please check that the card information is correct and valid. Your access will expire after ${accountData.fullAccessUntil}`,
      body: __(
        `We were unable to charge your card. To ensure uninterrupted access to all stories please check that the card information is correct and valid. Your access will expire after %{fullAccessUntil}`,
        'weWereUnableToChargeYourCard',
        { fullAccessUntil: accountData.fullAccessUntil }
      ),
    },

    'checkout-failure': {
      type: 'error',
      title: __('Payment unsuccessful', 'paymentUnsuccessful'),
      body: () => (
        <>
          Your credit card payment was not processed. Please{' '}
          <FeedbackLink>let us know</FeedbackLink> if you encountered an
          unexpected error.
        </>
      ),
    },

    // not currently used, but may bring back
    'new-account-email-verified': {
      type: 'success',
      title: __("You're ready to go", 'youreReadyToGo'),
      body: __(
        'Thank you for verifying your email.',
        'thankYouForVerifyingYourEmail'
      ),
    },

    'student-pricing': {
      type: 'success',
      title: __('Success!', 'successExclamation'),
      body: __(
        'Student discount pricing activated.',
        'studentDiscountPricingActivated'
      ),
    },

    'discount-pricing': {
      type: 'success',
      title: __('Success!', 'successExclamation'),
      body: __('Discount pricing activated.', 'discountPricingActivated'),
    },

    // no longer expected
    'purchase-gift': {
      type: 'success',
      title: __('Purchase complete', 'purchaseComplete'),
      // body: `Instructions to redeem gift coupon will appear shortly in your ${accountData.email} inbox. You can forward it to the giftee. The status of the coupon will update below when it is redeemed.`,
      body: '',
    },
  };

  return messages;
};
